import { useState, useEffect } from 'react';
// next
import NextLink from 'next/link';
import { useRouter } from 'next/router';
// components
import Logo from '../../components/Logo';

/* ============================================================================
   Menu do site publico no celular - visual novo (shadcn + Tailwind).

   Substitui `Drawer` + `Scrollbar` (simplebar) + `List`/`ListItemButton`/
   `Collapse` + `IconButtonAnimate` + `NavSectionVertical` do MUI por um painel
   deslizante com fundo escurecido, no mesmo padrao ja usado em
   layouts/dashboard/navbar/NavbarVertical.jsx.

   As props continuam `isOffset`, `isHome`, `navConfig`.

   O QUE CONTINUA IGUAL: botao abre a gaveta; item com filhos expande no lugar;
   "Documentation" abre em nova aba; a gaveta fecha ao trocar de rota; o item da
   rota atual fica destacado.

   O QUE MUDOU, e por que:

   1. Saiu o `NavSectionVertical` para os filhos. Ele era usado com um bloco de
      `sx` que sobrescrevia o ULTIMO item da lista para virar um retangulo de 200px
      com uma IMAGEM DE FUNDO externa (minimal-assets-api.vercel.app) e
      `display: none` em todo o conteudo dele - ou seja, o item "Dashboard" ficava
      sem texto e sem rotulo acessivel, dependendo de um dominio de terceiro para
      existir. Agora os filhos sao renderizados como lista de texto.

   2. O botao da gaveta ganhou `aria-label` e `aria-expanded` (era um
      IconButtonAnimate com so um icone dentro, sem nome para leitor de tela), e a
      gaveta fecha com Esc - ela cobre a tela inteira no celular.

   3. `isHome` nao pinta mais o botao de branco: `/` deixou de ser landing com foto
      escura, e branco sobre fundo claro nao aparece. A prop continua aceita.

   4. Os icones vem prontos de MenuConfig (`item.icon`, elementos <Iconify>) e sao
      apenas posicionados aqui - MenuConfig nao e alterado por este lote.
   ============================================================================ */

export default function MenuMobile({ isOffset, isHome, navConfig }) {
  const { pathname } = useRouter();

  const [expandido, setExpandido] = useState(false);

  const [gavetaAberta, setGavetaAberta] = useState(false);

  // Fecha a gaveta ao navegar. Sem isso a pagina trocava atras de um menu que
  // continuava aberto em cima dela.
  useEffect(() => {
    setGavetaAberta(false);
  }, [pathname]);

  // Esc fecha a gaveta.
  useEffect(() => {
    if (!gavetaAberta) return;
    const aoTeclar = (e) => e.key === 'Escape' && setGavetaAberta(false);
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [gavetaAberta]);

  return (
    <>
      <button
        type="button"
        onClick={() => setGavetaAberta(true)}
        aria-label="Abrir menu"
        aria-expanded={gavetaAberta}
        className="grid h-9 w-9 place-items-center rounded-lg border border-line bg-surface text-[16px] text-ink"
      >
        <span aria-hidden>☰</span>
      </button>

      {gavetaAberta && (
        <div className="fixed inset-0 z-[1200] bg-black/40" onClick={() => setGavetaAberta(false)} aria-hidden />
      )}

      <aside
        className={`fixed left-0 top-0 z-[1250] h-full w-[260px] overflow-y-auto border-r border-line bg-surface pb-10 transition-transform duration-200 ${
          gavetaAberta ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-hidden={!gavetaAberta}
        aria-label="Menu"
      >
        <div className="flex items-center justify-between px-4 py-4">
          <Logo sx={{ width: 44, height: 44 }} />
          <button
            type="button"
            onClick={() => setGavetaAberta(false)}
            aria-label="Fechar menu"
            className="grid h-7 w-7 place-items-center rounded-md border border-line text-muted-foreground"
          >
            <span aria-hidden>✕</span>
          </button>
        </div>

        <ul className="px-2">
          {navConfig.map((link) => (
            <MenuMobileItem
              key={link.title}
              item={link}
              isOpen={expandido}
              onOpen={() => setExpandido((v) => !v)}
            />
          ))}
        </ul>
      </aside>
    </>
  );
}

// ----------------------------------------------------------------------------

const CLASSES_ITEM =
  'flex w-full items-center gap-3 rounded-lg px-2.5 py-2.5 text-[12.5px] capitalize text-muted-foreground no-underline transition-colors hover:bg-soft';

function MenuMobileItem({ item, isOpen, onOpen }) {
  const { pathname } = useRouter();
  const { title, path, icon, children } = item;

  const isActive = pathname === path;

  if (children) {
    return (
      <li>
        <button type="button" onClick={onOpen} aria-expanded={isOpen} className={`${CLASSES_ITEM} bg-transparent`}>
          <span className="flex h-5 w-5 flex-none items-center justify-center">{icon}</span>
          <span className="flex-1 text-left">{title}</span>
          <span aria-hidden className="text-[10px] text-faint">
            {isOpen ? '▼' : '▶'}
          </span>
        </button>

        {isOpen && (
          <div className="mb-1 ml-3 border-l border-line pl-3">
            {children.map((grupo) => (
              <div key={grupo.subheader} className="mt-2">
                <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.07em] text-faint">{grupo.subheader}</p>
                <ul>
                  {grupo.items.map((sub) => (
                    <li key={sub.title}>
                      <NextLink href={sub.path} passHref>
                        <span
                          className={`block cursor-pointer py-1.5 text-[12.5px] no-underline ${
                            pathname === sub.path ? 'font-semibold text-ink' : 'text-muted-foreground'
                          }`}
                        >
                          {sub.title}
                        </span>
                      </NextLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </li>
    );
  }

  // Link externo: sai do react-router de proposito.
  if (title === 'Documentation') {
    return (
      <li>
        <a href={path} target="_blank" rel="noopener noreferrer" className={CLASSES_ITEM}>
          <span className="flex h-5 w-5 flex-none items-center justify-center">{icon}</span>
          <span className="flex-1">{title}</span>
        </a>
      </li>
    );
  }

  return (
    <li>
      <NextLink href={path} passHref>
        <span className={`${CLASSES_ITEM} cursor-pointer ${isActive ? 'bg-soft font-semibold text-primary' : ''}`}>
          <span className="flex h-5 w-5 flex-none items-center justify-center">{icon}</span>
          <span className="flex-1">{title}</span>
        </span>
      </NextLink>
    </li>
  );
}
